import { PLACEHOLDER } from './userAdapter';
import { formatDate } from './profileAdapter';

/** Pure mapping from GET admin/users/:userId/rewards to what the profile page renders. */

const STATES = {
  claimed: { label: 'Unlocked', tone: 'ok' },
  awaiting_code: { label: 'Code coming soon', tone: 'pending' },
  expired: { label: 'Expired', tone: 'neutral' },
  redeemed: { label: 'Redeemed', tone: 'ok' },
  revoked: { label: 'Revoked', tone: 'alert' },
  reached: { label: 'Reached (no coupon yet)', tone: 'pending' },
  active: { label: 'Next target', tone: 'pending' },
  locked: { label: 'Locked', tone: 'neutral' },
};

/** Unknown states are shown as-is (humanised) so new ones aren't hidden. */
export function getRewardState(state) {
  const known = STATES[state];
  if (known) return known;
  const text = String(state ?? '').replace(/[_-]+/g, ' ').trim();
  return { label: text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Unknown', tone: 'pending' };
}

/** Only https image URLs are rendered; anything else falls back to the brand initials. */
export function safeImageUrl(url) {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

function toLevelCard(target, approved) {
  const { level, scansRequired, previousScansRequired, state, coupon, reward } = target;
  const span = scansRequired - previousScansRequired;
  const scansToGo = Math.max(0, scansRequired - approved);
  return {
    level,
    scansRequired,
    brandName: coupon?.brandName?.trim() || PLACEHOLDER,
    logoUrl: safeImageUrl(coupon?.logoUrl),
    offerText: coupon?.offerText?.trim() || PLACEHOLDER,
    state: getRewardState(state),
    scansToGo,
    reached: scansToGo === 0,
    // This tier's part of the ladder only, from the previous tier to this one
    fill: span > 0 ? Math.max(0, Math.min(1, (approved - previousScansRequired) / span)) : scansToGo === 0 ? 1 : 0,
    reward: reward && {
      couponCode: reward.couponCode?.trim() || null,
      unlockedAt: formatDate(reward.unlockedAt) ?? PLACEHOLDER,
      expiresAt: formatDate(reward.expiresAt) ?? 'No expiry',
    },
  };
}

/**
 * @param {import('zod').infer<typeof import('./rewardSchema').rewardsSchema>} data
 */
export function toRewardsView(data) {
  const approved = data.scans.approved;
  const levels = data.targets.map((target) => toLevelCard(target, approved));
  const reached = levels.filter((level) => level.scansRequired <= approved);
  const currentLevel = reached.length ? Math.max(...reached.map((level) => level.level)) : null;
  const { activeTarget, allTiersCompleted } = data;

  return {
    approved,
    levels,
    maxScansRequired: levels.length ? Math.max(...levels.map((level) => level.scansRequired)) : 0,
    currentLevelLabel: currentLevel === null ? 'No level yet' : `Level ${currentLevel}`,
    reachedCount: reached.length,
    unlockedCount: data.targets.filter((target) => target.reward !== null).length,
    next: activeTarget && {
      level: activeTarget.level,
      scansRemaining: activeTarget.scansRemaining,
      label: `Level ${activeTarget.level} · ${activeTarget.coupon?.brandName?.trim() || PLACEHOLDER}`,
    },
    allTiersCompleted,
    hasActivePackage: data.hasActivePackage,
  };
}
